/**
 * Pre-save diff review — `POST /admin/data/dscr-pricing/{cpId}/diff` (multipart
 * xlsx). Uploads a NEW workbook and diffs it against the counterparty's stored
 * current pricing WITHOUT writing anything, then optionally commits the new
 * parse as a version.
 *
 * The response is `{ cpId, currentVersion, diff, newParsed }` where `diff` is:
 *   ladders:     { [tier]: [{coupon, change: added|removed|modified, fields?}] }
 *   tierMargins: [{tier, old, new}]
 *   llpa:        [{table, label, old, new}]
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import { useSnackbar } from "notistack";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { adminDscrPricingApi } from "../../../api/adminDataApi";
import { AdminEmpty } from "../shared/AdminScreenShell";
import { PricingField } from "./pricingUi";
import GridAgentReport from "./GridAgentReport";

const CHANGE_COLORS = {
  added: { bg: "#dcfce7", fg: "#166534" },
  removed: { bg: "#fee2e2", fg: "#991b1b" },
  modified: { bg: "#fef3c7", fg: "#92400e" },
};

const fmt = (v) => (v == null || v === "" ? "—" : String(v));

export default function DscrDiffDialog({
  open,
  cpId,
  counterparty,
  onClose,
  onCommit,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) return;
    setFile(null);
    setResult(null);
    setBusy(false);
    setNote("");
  }, [open]);

  const run = useCallback(() => {
    if (!file || !cpId) return;
    setBusy(true);
    adminDscrPricingApi
      .diff(cpId, file)
      .then(setResult)
      .catch((err) =>
        enqueueSnackbar(
          err?.response?.data?.detail || "Couldn't diff the workbook",
          { variant: "error" },
        ),
      )
      .finally(() => setBusy(false));
  }, [file, cpId, enqueueSnackbar]);

  const counts = useMemo(() => {
    const d = result?.diff;
    if (!d) return null;
    const ladderRows = Object.values(d.ladders || {}).reduce(
      (n, arr) => n + arr.length,
      0,
    );
    return {
      ladderRows,
      margins: (d.tierMargins || []).length,
      llpa: (d.llpa || []).length,
      total: ladderRows + (d.tierMargins || []).length + (d.llpa || []).length,
    };
  }, [result]);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
        Diff a new workbook — {counterparty || cpId}
      </DialogTitle>
      <DialogContent dividers>
        <Flx ac g={1.5} sx={{ mb: 2 }} wrap>
          <Button variant="outlined" component="label" disabled={busy}>
            {file ? "Change workbook" : "Choose .xlsx"}
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) {
                  setFile(f);
                  setResult(null);
                }
              }}
            />
          </Button>
          <Txt sx={{ fontSize: 12.5, color: file ? "#0f172a" : "#94a3b8" }}>
            {file ? file.name : "No file selected"}
          </Txt>
          <Button variant="contained" onClick={run} disabled={busy || !file}>
            Run diff
          </Button>
          {result ? (
            <Txt sx={{ fontSize: 12, color: "#64748b" }}>
              vs stored v{result.currentVersion}
            </Txt>
          ) : null}
        </Flx>

        {busy ? <LinearProgress sx={{ mb: 2 }} /> : null}

        {/* The agent reviews the INCOMING workbook too, so a re-import is
            checked before it overwrites — warnings sit above the deltas. */}
        {result?.gridAgent ? (
          <Box sx={{ mb: 2 }}>
            <GridAgentReport gridAgent={result.gridAgent} />
          </Box>
        ) : null}

        {!result ? (
          <AdminEmpty>
            Upload the new sheet to see per-coupon, LLPA and tier-margin deltas
            before anything is written.
          </AdminEmpty>
        ) : counts?.total === 0 ? (
          <AdminEmpty>
            No differences — this workbook parses identically to the stored
            version.
          </AdminEmpty>
        ) : (
          <Flx column g={2.5}>
            <Flx g={1} wrap>
              <Chip
                size="small"
                label={`${counts.ladderRows} ladder change${counts.ladderRows === 1 ? "" : "s"}`}
                sx={{ fontSize: 11, fontWeight: 600 }}
              />
              <Chip
                size="small"
                label={`${counts.margins} tier margin change${counts.margins === 1 ? "" : "s"}`}
                sx={{ fontSize: 11, fontWeight: 600 }}
              />
              <Chip
                size="small"
                label={`${counts.llpa} LLPA change${counts.llpa === 1 ? "" : "s"}`}
                sx={{ fontSize: 11, fontWeight: 600 }}
              />
            </Flx>

            {(result.diff.tierMargins || []).length ? (
              <Section title="Tier margins">
                {result.diff.tierMargins.map((m) => (
                  <Row
                    key={m.tier}
                    label={m.tier}
                    detail={`${fmt(m.old)} → ${fmt(m.new)}`}
                    change="modified"
                  />
                ))}
              </Section>
            ) : null}

            {Object.entries(result.diff.ladders || {}).map(([tier, rows]) => (
              <Section key={tier} title={`${tier} ladder (${rows.length})`}>
                {rows.map((r, i) => (
                  <Row
                    key={`${tier}-${r.coupon}-${i}`}
                    label={`coupon ${r.coupon}`}
                    change={r.change}
                    detail={
                      r.change === "modified"
                        ? Object.entries(r.fields || {})
                            .map(
                              ([k, v]) =>
                                `${k}: ${fmt(v.old)} → ${fmt(v.new)}`,
                            )
                            .join("   ·   ")
                        : r.change === "added"
                          ? "new coupon row"
                          : "removed"
                    }
                  />
                ))}
              </Section>
            ))}

            {(result.diff.llpa || []).length ? (
              <Section title="LLPA rows">
                {result.diff.llpa.map((l, i) => (
                  <Row
                    key={`${l.table}-${l.label}-${i}`}
                    label={`${l.table} · ${l.label}`}
                    change="modified"
                    detail={`${JSON.stringify(l.old)} → ${JSON.stringify(l.new)}`}
                  />
                ))}
              </Section>
            ) : null}

            <PricingField label="Change note">
              <TextField
                size="small"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={`new sheet: ${file?.name || ""}`}
                fullWidth
              />
            </PricingField>
          </Flx>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={busy}>
          Close
        </Button>
        <Button
          variant="contained"
          disabled={busy || !result?.newParsed}
          onClick={() =>
            onCommit?.(
              result.newParsed,
              note || `new sheet: ${file?.name || "workbook"}`,
            )
          }
        >
          Save new version
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DscrDiffDialog.propTypes = {
  open: PropTypes.bool,
  cpId: PropTypes.string,
  counterparty: PropTypes.string,
  onClose: PropTypes.func,
  onCommit: PropTypes.func,
};

const Section = ({ title, children }) => (
  <Box>
    <Txt
      sx={{
        fontSize: 12,
        fontWeight: 700,
        color: "#0f172a",
        display: "block",
        mb: 1,
      }}
    >
      {title}
    </Txt>
    <Flx column g={0.5}>{children}</Flx>
  </Box>
);

const Row = ({ label, detail, change }) => {
  const c = CHANGE_COLORS[change] || CHANGE_COLORS.modified;
  return (
    <Flx
      ac
      g={1}
      sx={{
        px: 1.5,
        py: 0.75,
        border: "1px solid #e5e7eb",
        borderRadius: "6px",
        bgcolor: "#fff",
      }}
      wrap
    >
      <Chip
        size="small"
        label={change}
        sx={{
          bgcolor: c.bg,
          color: c.fg,
          fontWeight: 700,
          fontSize: 10.5,
          height: 18,
        }}
      />
      <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: "#0f172a", minWidth: 160 }}>
        {label}
      </Txt>
      <Txt sx={{ fontSize: 12, color: "#475569", flex: 1 }}>{detail}</Txt>
    </Flx>
  );
};
